/**
 * Scheda "Lavori previsti" del progetto (richiesta di Andrea del 6/10/2026).
 *
 * Quello che abbiamo promesso al cliente, di solito le righe del preventivo,
 * ognuno col suo stato. Quando un lavoro è fermo si dice chi lo tiene fermo:
 * il cliente (manca un suo materiale, una risposta) oppure noi. Da qui parte
 * il resoconto al cliente, che resta registrato nel Diario: se un giorno
 * contesta, la comunicazione c'è.
 *
 * I lavori sono task con `inScope`: si aprono col dettaglio task di sempre e
 * compaiono anche nella scheda Task. Solo loro fanno l'avanzamento del progetto.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { Modal } from '../../components/ui/Modal';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { Avatar } from '../../components/ui/Avatar';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { useIsMobile } from '../../hooks/useIsMobile';
import { fmtDate, fmtEur } from '../../lib/utils';
import './lavori.css';

export interface LavoroTask {
  id: string;
  title: string;
  status: string;
  dueDate: string | null;
  assignee: { id?: string; firstName: string; lastName: string; initials: string } | null;
  blockedReason: string | null;
  inScope?: boolean;
  quoteRef?: string | null;
  blockedBy?: 'CLIENTE' | 'NOI' | null;
  blockedSince?: string | null;
  completedAt?: string | null;
}

export interface ComunicazioneProgetto {
  id: string;
  subject: string;
  tag: string | null;
  occurredAt: string;
  toText: string | null;
}

/** Deve restare uguale a TAG_RESOCONTO in apps/api/src/modules/projects/lavori.service.ts. */
// Le esportazioni qui sotto servono alla versione del telefono (LavoriMobile):
// stessi stati, stesse finestre, stesse chiamate. Sul computer non cambia niente.
export const TAG_RESOCONTO = 'Resoconto lavori';

export type Stato = 'FERMO_CLIENTE' | 'FERMO_NOI' | 'FERMO' | 'IN_PROGRESS' | 'REVIEW' | 'TODO' | 'DONE';
type Tono = 'default' | 'ok' | 'warn' | 'alert' | 'info' | 'ghost' | 'active';

/** Ordine = come si leggono: prima quello che aspetta qualcuno, in fondo il fatto. */
export const STATI: Record<Stato, { breve: string; ordine: number; tono: Tono }> = {
  FERMO_CLIENTE: { breve: 'Aspettiamo il cliente', ordine: 0, tono: 'warn' },
  FERMO_NOI:     { breve: 'Fermo da noi',          ordine: 1, tono: 'alert' },
  // Fermo senza sapere chi: succede trascinandolo in "Bloccati" dalla bacheca dei task.
  FERMO:         { breve: 'Fermo',                 ordine: 1, tono: 'alert' },
  IN_PROGRESS:   { breve: 'In corso',              ordine: 2, tono: 'active' },
  REVIEW:        { breve: 'Da approvare',          ordine: 3, tono: 'default' },
  TODO:          { breve: 'Da fare',               ordine: 4, tono: 'ghost' },
  DONE:          { breve: 'Fatto',                 ordine: 5, tono: 'ok' },
};

export function statoDi(t: LavoroTask): Stato {
  if (t.status === 'BLOCKED') return t.blockedBy === 'CLIENTE' ? 'FERMO_CLIENTE' : t.blockedBy === 'NOI' ? 'FERMO_NOI' : 'FERMO';
  return (t.status in STATI ? t.status : 'TODO') as Stato;
}

export const giorniDa = (iso: string | null | undefined) =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null;
export const daQuanto = (g: number | null) => (g == null ? '' : g === 0 ? 'da oggi' : g === 1 ? 'da 1 giorno' : `da ${g} giorni`);

export function apriTask(id: string) {
  useModalStore.getState().open('taskDetail', { taskId: id });
}

export type Aggiornamento = {
  status?: string;
  blockedBy?: 'CLIENTE' | 'NOI' | null;
  blockedReason?: string | null;
  inScope?: boolean;
};

export function LavoriTab({ projectId, tasks, communications }: {
  projectId: string;
  tasks: LavoroTask[];
  communications: ComunicazioneProgetto[];
}) {
  const qc = useQueryClient();
  const [importa, setImporta] = useState(false);
  const [resoconto, setResoconto] = useState(false);
  const [fermo, setFermo] = useState<{ lavoro: LavoroTask; chi: 'CLIENTE' | 'NOI' } | null>(null);
  const [nuovo, setNuovo] = useState('');
  // Senza lavori la tabella non c'è: "Scrivi un lavoro a mano" apre il campo dentro il riquadro vuoto.
  const [aMano, setAMano] = useState(false);
  const campoNuovo = useRef<HTMLInputElement>(null);

  const lavori = useMemo(
    () => tasks
      .filter((t) => t.inScope)
      .map((t, i) => ({ t, i, s: statoDi(t) }))
      // A parità di stato resta l'ordine del preventivo.
      .sort((a, b) => STATI[a.s].ordine - STATI[b.s].ordine || a.i - b.i),
    [tasks],
  );
  const totale = lavori.length;
  const fatti = lavori.filter((l) => l.s === 'DONE').length;
  const attesaCliente = lavori.filter((l) => l.s === 'FERMO_CLIENTE');
  const fermiNoi = lavori.filter((l) => l.s === 'FERMO_NOI' || l.s === 'FERMO');
  const piuVecchio = attesaCliente.reduce<number | null>((max, l) => {
    const g = giorniDa(l.t.blockedSince);
    return g != null && (max == null || g > max) ? g : max;
  }, null);
  const ultimo = communications
    .filter((c) => c.tag === TAG_RESOCONTO)
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))[0];
  const giorniUltimo = giorniDa(ultimo?.occurredAt);

  const invalida = () => qc.invalidateQueries({ queryKey: ['project', projectId] });

  const aggiorna = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Aggiornamento }) => api.patch(`/projects/${projectId}/lavori/${id}`, body),
    onSuccess: invalida,
    onError: (e: any) => toast.alert('Lavoro non aggiornato', e?.message ?? 'Riprova'),
  });

  const aggiungi = useMutation({
    mutationFn: (title: string) => api.post(`/projects/${projectId}/lavori`, { voci: [{ title }] }),
    onSuccess: () => { setNuovo(''); invalida(); },
    onError: (e: any) => toast.alert('Lavoro non aggiunto', e?.message ?? 'Riprova'),
  });

  const cambiaStato = (t: LavoroTask, s: Stato) => {
    if (s === 'FERMO_CLIENTE' || s === 'FERMO_NOI') { setFermo({ lavoro: t, chi: s === 'FERMO_CLIENTE' ? 'CLIENTE' : 'NOI' }); return; }
    aggiorna.mutate({ id: t.id, body: { status: s === 'FERMO' ? 'BLOCKED' : s } });
  };

  const scriviAMano = () => { setAMano(true); setTimeout(() => campoNuovo.current?.focus(), 0); };

  const campoAggiungi = (
    <input
      ref={campoNuovo}
      className="inp w-full"
      placeholder="Aggiungi un lavoro previsto, Invio per salvarlo…"
      value={nuovo}
      onChange={(e) => setNuovo(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); if (nuovo.trim() && !aggiungi.isPending) aggiungi.mutate(nuovo.trim()); }
        else if (e.key === 'Escape') setNuovo('');
      }}
    />
  );

  return (
    <div className="lv">
      {totale > 0 && (
        <div className="stat-band">
          <div>
            <div className="st-label">Lavori previsti fatti</div>
            <div className="st-value"><AnimatedNumber value={fatti} /><span className="u">&nbsp;su {totale}</span></div>
            <div className="st-delta">{Math.round((fatti * 100) / totale)}% del progetto</div>
          </div>
          <div>
            <div className="st-label">Fermi: aspettiamo il cliente</div>
            <div className="st-value"><AnimatedNumber value={attesaCliente.length} /></div>
            <div className="st-delta" style={piuVecchio != null && piuVecchio >= 7 ? { color: 'var(--alert)' } : undefined}>
              {piuVecchio == null
                ? 'nessuno'
                : attesaCliente.length === 1 ? `fermo ${daQuanto(piuVecchio)}` : `il più vecchio ${daQuanto(piuVecchio)}`}
            </div>
          </div>
          <div>
            <div className="st-label">Fermi: tocca a noi</div>
            <div className="st-value"><AnimatedNumber value={fermiNoi.length} /></div>
            <div className="st-delta">{fermiNoi.length ? 'da sbloccare' : 'nessuno'}</div>
          </div>
          <div>
            <div className="st-label">Ultimo resoconto al cliente</div>
            <div className="st-value">
              {giorniUltimo == null ? '—' : giorniUltimo === 0 ? 'oggi' : <><AnimatedNumber value={giorniUltimo} /><span className="u">gg fa</span></>}
            </div>
            <div className="st-delta lv-delta-tronca">{ultimo ? `${fmtDate(ultimo.occurredAt)}${ultimo.toText ? ` a ${ultimo.toText}` : ''}` : 'mai mandato'}</div>
          </div>
        </div>
      )}

      <div className="ed-section">
        <div className="data-hd">
          <div className="flex items-center gap-2">
            <span className="t-micro">Lavori previsti</span>
            <span className="mono mute text-[12px]">{totale}</span>
          </div>
          {totale > 0 && (
            <div className="flex gap-2 d-only">
              <button className="btn btn-sm btn-ghost" onClick={() => setImporta(true)}><Icon name="file" size={12} /> Carica dal preventivo</button>
              <button className="btn btn-sm btn-primary" onClick={() => setResoconto(true)}><Icon name="send" size={12} /> Manda il resoconto al cliente</button>
            </div>
          )}
        </div>

        {totale === 0 ? (
          <div className="lv-vuoto">
            <div className="lv-vuoto-t">Nessun lavoro previsto per ora</div>
            <div className="lv-vuoto-d">
              Carica le righe del preventivo: ogni riga diventa un lavoro con il suo stato, e il resoconto al cliente si scrive da solo.
            </div>
            {aMano ? (
              <div className="lv-vuoto-campo">{campoAggiungi}</div>
            ) : (
              <div className="lv-vuoto-az">
                <button className="btn btn-primary d-only" onClick={() => setImporta(true)}><Icon name="file" size={12} /> Carica dal preventivo</button>
                <button className="btn" onClick={scriviAMano}>Scrivi un lavoro a mano</button>
              </div>
            )}
          </div>
        ) : (
        <table className="tbl tbl--ed tbl--card lv-tbl">
          <thead>
            <tr>
              <th style={{ width: 30 }} /><th>Lavoro</th><th>A che punto è</th><th>Assegnato</th><th className="num">Scadenza</th>
            </tr>
          </thead>
          <tbody>
            {lavori.map(({ t, s }) => {
              const fermoDa = s === 'FERMO_CLIENTE' || s === 'FERMO_NOI' || s === 'FERMO' ? giorniDa(t.blockedSince) : null;
              return (
                <tr key={t.id} onClick={() => apriTask(t.id)} className={s === 'DONE' ? 'lv-fatto' : undefined}>
                  {/* La spunta è il gesto più frequente: al computer sta qui, sul
                      telefono si fa dallo stato (un quadratino da 14px in una
                      scheda sarebbe un bersaglio, non un comando). */}
                  <td data-m="off" onClick={(e) => { e.stopPropagation(); aggiorna.mutate({ id: t.id, body: { status: s === 'DONE' ? 'TODO' : 'DONE' } }); }}>
                    <div className={`lv-spunta${s === 'DONE' ? ' is-on' : ''}`} role="checkbox" aria-checked={s === 'DONE'} aria-label={`Segna "${t.title}" come fatto`}>
                      {s === 'DONE' && <Icon name="check" size={8} />}
                    </div>
                  </td>
                  <td data-m="hd">
                    <div className="lv-titolo">{t.title}</div>
                    {(s === 'FERMO_CLIENTE' || s === 'FERMO_NOI' || s === 'FERMO') && (
                      <div className={`lv-motivo${s === 'FERMO_CLIENTE' ? ' is-cliente' : ''}`}>
                        {s === 'FERMO_CLIENTE' ? 'Aspettiamo dal cliente: ' : 'Fermo perché: '}
                        {t.blockedReason?.trim() || 'motivo non scritto'}
                        {fermoDa != null && <span className="lv-da"> · {daQuanto(fermoDa)}</span>}
                      </div>
                    )}
                    {t.quoteRef && <div className="lv-fonte">{t.quoteRef}</div>}
                  </td>
                  <td data-m="row" data-l="A che punto è" onClick={(e) => e.stopPropagation()}>
                    <MenuStato
                      stato={s}
                      onScegli={(n) => cambiaStato(t, n)}
                      onApri={() => apriTask(t.id)}
                      onTogli={() => aggiorna.mutate({ id: t.id, body: { inScope: false } }, {
                        onSuccess: () => toast.ok('Tolto dai lavori previsti', `"${t.title}" resta tra i task del progetto.`),
                      })}
                    />
                  </td>
                  <td data-m="key" data-l="Assegnato">{t.assignee
                    ? <div className="flex gap-1.5 items-center"><Avatar initials={t.assignee.initials} size={20} />{t.assignee.firstName[0]}. {t.assignee.lastName}</div>
                    : <span className="mute-2">—</span>}</td>
                  <td data-m="key" data-l="Scadenza" className="num mono mute">{fmtDate(t.dueDate)}</td>
                </tr>
              );
            })}
            <tr className="pj-addrow">
              <td data-m="off"><Icon name="plus" size={10} className="mute" /></td>
              <td data-m="row" colSpan={4}>{campoAggiungi}</td>
            </tr>
          </tbody>
        </table>
        )}
      </div>

      {/* Sul telefono le due azioni stanno in fondo, dove arriva il pollice (mobile.md §4). */}
      <div className="m-actionbar m-only">
        <button className="btn" onClick={() => setImporta(true)}><Icon name="file" size={12} /> Dal preventivo</button>
        {totale > 0 && (
          <button className="btn btn-primary" onClick={() => setResoconto(true)}><Icon name="send" size={12} /> Resoconto al cliente</button>
        )}
      </div>

      {importa && <SuTutto><CaricaDalPreventivo projectId={projectId} onClose={() => setImporta(false)} /></SuTutto>}
      {resoconto && <SuTutto><ResocontoAlCliente projectId={projectId} onClose={() => setResoconto(false)} /></SuTutto>}
      {fermo && (
        <SuTutto>
          <FermoDialog
            lavoro={fermo.lavoro}
            chi={fermo.chi}
            onClose={() => setFermo(null)}
            onConferma={(motivo) => aggiorna.mutate(
              { id: fermo.lavoro.id, body: { status: 'BLOCKED', blockedBy: fermo.chi, blockedReason: motivo } },
              { onSuccess: () => setFermo(null) },
            )}
            inCorso={aggiorna.isPending}
          />
        </SuTutto>
      )}
    </div>
  );
}

/**
 * Le finestre di questa scheda vanno su <body>. Dentro la pagina, il
 * `transform` dell'animazione d'ingresso di `.page` diventa il riferimento del
 * `position: fixed` del fondo scuro, che copriva solo il riquadro della pagina
 * (mobile.md §4 e §6). I modali del gestionale stanno già fuori, in ModalsRoot.
 */
export function SuTutto({ children }: { children: ReactNode }) {
  return createPortal(children, document.body);
}

// ---------------------------------------------------------------------------
// Il menu dello stato: tendina al computer, pannello dal basso sul telefono
// ---------------------------------------------------------------------------

export const VOCI_STATO: Array<{ s: Stato; label: string; icon: IconName }> = [
  { s: 'TODO', label: 'Da fare', icon: 'clock' },
  { s: 'IN_PROGRESS', label: 'In corso', icon: 'zap' },
  { s: 'REVIEW', label: 'Consegnato, da approvare dal cliente', icon: 'send' },
  { s: 'DONE', label: 'Fatto', icon: 'check' },
  { s: 'FERMO_CLIENTE', label: 'Fermo: aspettiamo il cliente…', icon: 'user' },
  { s: 'FERMO_NOI', label: 'Fermo: tocca a noi…', icon: 'alert' },
];

function MenuStato({ stato, onScegli, onApri, onTogli }: {
  stato: Stato;
  onScegli: (s: Stato) => void;
  onApri: () => void;
  onTogli: () => void;
}) {
  const [aperto, setAperto] = useState(false);
  const isMobile = useIsMobile();
  const info = STATI[stato];
  const voci = VOCI_STATO.filter((v) => v.s !== stato);
  const scegli = (fn: () => void) => () => { setAperto(false); fn(); };

  const righeTelefono = (
    <div role="menu" aria-label="Cambia lo stato del lavoro">
      {voci.map((v) => (
        <button key={v.s} type="button" role="menuitem" className="m-sheet-row m-act-row" onClick={scegli(() => onScegli(v.s))}>
          <Icon name={v.icon} size={16} className="m-act-ico" />
          <span className="m-sheet-row-lbl">{v.label}</span>
        </button>
      ))}
      <button type="button" role="menuitem" className="m-sheet-row m-act-row" onClick={scegli(onApri)}>
        <Icon name="eye" size={16} className="m-act-ico" />
        <span className="m-sheet-row-lbl">Apri il task del lavoro</span>
      </button>
      <div className="m-act-danger">
        <button type="button" role="menuitem" className="m-sheet-row m-act-row" onClick={scegli(onTogli)}>
          <Icon name="x" size={16} className="m-act-ico" />
          <span className="m-sheet-row-lbl">Togli dai lavori previsti</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative lv-stato">
      <button
        type="button"
        className={`chip chip-btn lv-stato-btn${info.tono !== 'default' ? ` ${info.tono}` : ''}`}
        onClick={() => setAperto((v) => !v)}
        aria-haspopup={isMobile ? 'dialog' : 'menu'}
        aria-expanded={aperto}
      >
        {info.breve} <Icon name="chevronD" size={9} />
      </button>
      {isMobile ? (
        <MobileSheet open={aperto} onClose={() => setAperto(false)} title={`Lavoro: ${info.breve.toLowerCase()}`}>
          {righeTelefono}
        </MobileSheet>
      ) : aperto && (
        <>
          <div className="fixed inset-0 z-[60] actions-menu-backdrop" onClick={() => setAperto(false)} />
          <div className="absolute left-0 mt-1 z-[61] border border-line bg-white shadow-lg overflow-hidden actions-menu-pop" style={{ minWidth: 280, borderRadius: 'var(--r-card)' }} role="menu">
            {voci.map((v) => (
              <div key={v.s} role="menuitem" className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-paper-2 cursor-pointer transition text-[13px] actions-menu-row" onClick={scegli(() => onScegli(v.s))}>
                <Icon name={v.icon} size={12} className="mute" />
                <span>{v.label}</span>
              </div>
            ))}
            <div className="border-t border-line">
              <div role="menuitem" className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-paper-2 cursor-pointer transition text-[13px] actions-menu-row" onClick={scegli(onApri)}>
                <Icon name="eye" size={12} className="mute" />
                <span>Apri il task del lavoro</span>
              </div>
              <div role="menuitem" className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-alert-bg cursor-pointer transition text-[13px] text-alert actions-menu-row" onClick={scegli(onTogli)}>
                <Icon name="x" size={12} />
                <span>Togli dai lavori previsti</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fermo: chi e perché
// ---------------------------------------------------------------------------

export function FermoDialog({ lavoro, chi, onClose, onConferma, inCorso }: {
  lavoro: LavoroTask;
  chi: 'CLIENTE' | 'NOI';
  onClose: () => void;
  onConferma: (motivo: string) => void;
  inCorso: boolean;
}) {
  const [motivo, setMotivo] = useState(lavoro.blockedReason ?? '');
  const cliente = chi === 'CLIENTE';
  const manca = cliente && !motivo.trim();
  return (
    <Modal
      kicker={lavoro.title}
      title={cliente ? 'Fermo: aspettiamo il cliente' : 'Fermo: tocca a noi'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" disabled={manca || inCorso} onClick={() => onConferma(motivo.trim())}>Segna il lavoro fermo</button>
        </>
      }
    >
      <label className="field-label" htmlFor="lv-motivo">{cliente ? 'Cosa aspettiamo dal cliente' : 'Perché è fermo'}</label>
      <textarea
        id="lv-motivo"
        className="inp lv-area"
        rows={3}
        autoFocus
        value={motivo}
        placeholder={cliente ? 'Per esempio: gli argomenti dei video del mese' : 'Per esempio: manca la persona che lo segue'}
        onChange={(e) => setMotivo(e.target.value)}
      />
      <div className="field-hint">
        {cliente
          ? 'Va nel resoconto al cliente così come lo scrivi, insieme al giorno da cui lo aspettiamo.'
          : 'Resta interno: nel resoconto al cliente questo lavoro risulta in corso.'}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Carica dal preventivo
// ---------------------------------------------------------------------------

interface RigaPreventivo { titolo: string; descrizione: string | null; quantita: number | null; caricata: boolean }
interface Preventivo {
  origine: 'FATTURE_IN_CLOUD' | 'GESTIONALE';
  id: string;
  riferimento: string;
  oggetto: string | null;
  totaleNetto: number | null;
  righe: RigaPreventivo[];
}

export function CaricaDalPreventivo({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['project-preventivi', projectId],
    queryFn: () => api.get<{ preventivi: Preventivo[]; avviso: string | null }>(`/projects/${projectId}/preventivi`),
    staleTime: 60_000,
  });
  const [scelto, setScelto] = useState<string | null>(null);
  const [righe, setRighe] = useState<Array<{ on: boolean; titolo: string; descrizione: string | null; caricata: boolean; quantita: number | null }>>([]);
  const preventivo = data?.preventivi.find((q) => q.id === scelto) ?? null;

  useEffect(() => {
    if (!preventivo) { setRighe([]); return; }
    setRighe(preventivo.righe.map((r) => ({ on: !r.caricata, titolo: r.titolo, descrizione: r.descrizione, caricata: r.caricata, quantita: r.quantita })));
  }, [preventivo]);

  const carica = useMutation({
    mutationFn: () => api.post<{ creati: number }>(`/projects/${projectId}/lavori`, {
      voci: righe.filter((r) => r.on && r.titolo.trim()).map((r) => ({ title: r.titolo.trim(), description: r.descrizione, quoteRef: preventivo!.riferimento })),
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      qc.invalidateQueries({ queryKey: ['project-preventivi', projectId] });
      toast.ok(r.creati === 1 ? '1 lavoro caricato' : `${r.creati} lavori caricati`, preventivo!.riferimento);
      onClose();
    },
    onError: (e: any) => toast.alert('Lavori non caricati', e?.message ?? 'Riprova'),
  });
  const quante = righe.filter((r) => r.on && r.titolo.trim()).length;

  return (
    <Modal
      size="lg"
      kicker="Lavori previsti"
      title={preventivo ? preventivo.riferimento : 'Carica dal preventivo'}
      onClose={onClose}
      footer={preventivo ? (
        <>
          <button className="btn" onClick={() => setScelto(null)}><Icon name="arrowLeft" size={12} /> Altro preventivo</button>
          <button className="btn btn-primary" disabled={!quante || carica.isPending} onClick={() => carica.mutate()}>
            {quante === 1 ? 'Carica 1 lavoro' : `Carica ${quante} lavori`}
          </button>
        </>
      ) : <button className="btn" onClick={onClose}>Chiudi</button>}
    >
      {isLoading && <div className="lv-attesa">Leggo i preventivi del cliente da Fatture in Cloud…</div>}
      {isError && <div className="lv-avviso">{(error as any)?.message ?? 'Non riesco a leggere i preventivi.'}</div>}
      {data?.avviso && <div className="lv-avviso">{data.avviso}</div>}

      {data && !preventivo && (
        data.preventivi.length ? (
          <div className="lv-prev-lista" role="list">
            {data.preventivi.map((q) => (
              <button key={`${q.origine}-${q.id}`} type="button" role="listitem" className="lv-prev" onClick={() => setScelto(q.id)}>
                <span className="lv-prev-tit">{q.riferimento}</span>
                {q.oggetto && <span className="lv-prev-ogg">{q.oggetto}</span>}
                <span className="lv-prev-meta">
                  {q.righe.length === 1 ? '1 riga' : `${q.righe.length} righe`}
                  {q.totaleNetto != null && <> · € {fmtEur(q.totaleNetto)} netti</>}
                  {q.righe.some((r) => r.caricata) && <> · già caricato in parte</>}
                </span>
                <Icon name="chevronR" size={12} className="lv-prev-ico" />
              </button>
            ))}
          </div>
        ) : !data.avviso && <div className="lv-attesa">Non ci sono preventivi per questo cliente.</div>
      )}

      {preventivo && (
        <>
          {preventivo.oggetto && <div className="lv-prev-ogg lv-prev-ogg--hd">{preventivo.oggetto}</div>}
          <div className="field-hint lv-hint">Ogni riga spuntata diventa un lavoro. Puoi riscrivere il nome come lo diresti al cliente.</div>
          <div className="lv-righe">
            {righe.map((r, i) => (
              <div key={i} className={`lv-riga${r.on ? '' : ' is-off'}`}>
                <input
                  type="checkbox"
                  className="lv-check"
                  checked={r.on}
                  aria-label={`Carica "${r.titolo}"`}
                  onChange={(e) => setRighe((v) => v.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))}
                />
                <input
                  className="inp lv-riga-inp"
                  value={r.titolo}
                  aria-label="Nome del lavoro"
                  onChange={(e) => setRighe((v) => v.map((x, j) => (j === i ? { ...x, titolo: e.target.value } : x)))}
                />
                {r.quantita != null && r.quantita !== 1 && <span className="lv-qta mono">× {r.quantita}</span>}
                {r.caricata && <Chip tone="ghost">già caricato</Chip>}
              </div>
            ))}
            {!righe.length && <div className="lv-attesa">Questo preventivo non ha righe da caricare.</div>}
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Resoconto al cliente
// ---------------------------------------------------------------------------

interface Bozza {
  destinatari: Array<{ nome: string; email: string; ruolo: string | null }>;
  proposti: string[];
  oggetto: string;
  testo: string;
  lavori: number;
  invioDalGestionale: boolean;
}

export function ResocontoAlCliente({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['project-resoconto', projectId],
    queryFn: () => api.get<Bozza>(`/projects/${projectId}/resoconto`),
    staleTime: 0,
    gcTime: 0,
  });
  const [a, setA] = useState<string[]>([]);
  const [altro, setAltro] = useState('');
  const [oggetto, setOggetto] = useState('');
  const [testo, setTesto] = useState('');
  useEffect(() => {
    if (!data) return;
    setA(data.proposti);
    setOggetto(data.oggetto);
    setTesto(data.testo);
  }, [data]);

  const altroValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(altro.trim());
  const destinatari = [...a, ...(altroValido ? [altro.trim()] : [])];

  const registra = useMutation({
    mutationFn: (modo: 'invia' | 'registra') => api.post(`/projects/${projectId}/resoconto`, { modo, a: destinatari, cc: [], oggetto, testo }),
    onSuccess: (_r, modo) => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      toast.ok(
        modo === 'invia' ? 'Resoconto mandato' : 'Resoconto segnato come mandato',
        modo === 'invia' ? destinatari.join(', ') : 'Lo trovi nel Diario del progetto.',
      );
      onClose();
    },
    onError: (e: any) => toast.alert('Resoconto non registrato', e?.message ?? 'Riprova'),
  });

  const copia = async () => {
    try {
      await navigator.clipboard.writeText(testo);
      toast.ok('Testo copiato', 'Incollalo nella mail o su WhatsApp, poi segnalo come mandato.');
    } catch {
      toast.alert('Copia non riuscita', 'Seleziona il testo e copialo a mano.');
    }
  };

  const puoInviare = !!data?.invioDalGestionale && destinatari.length > 0;

  return (
    <Modal
      size="lg"
      kicker="Lavori previsti"
      title="Resoconto al cliente"
      onClose={onClose}
      footer={data ? (
        <div className="lv-res-ft">
          <button className="btn" onClick={copia}><Icon name="copy" size={12} /> Copia il testo</button>
          <button className={data.invioDalGestionale ? 'btn' : 'btn btn-primary'} disabled={registra.isPending} onClick={() => registra.mutate('registra')}>
            <Icon name="check" size={12} /> Segna come mandato
          </button>
          {data.invioDalGestionale && (
            <button className="btn btn-primary" disabled={!puoInviare || registra.isPending} onClick={() => registra.mutate('invia')}>
              <Icon name="send" size={12} /> Manda dal gestionale
            </button>
          )}
        </div>
      ) : <button className="btn" onClick={onClose}>Chiudi</button>}
    >
      {isLoading && <div className="lv-attesa">Preparo il resoconto…</div>}
      {isError && <div className="lv-avviso">{(error as any)?.message ?? 'Non riesco a preparare il resoconto.'}</div>}
      {data && (
        <div className="lv-res">
          {!data.invioDalGestionale && (
            <div className="lv-avviso">
              Da qui la posta non parte: il gestionale non ha un server di posta. Copia il testo, mandalo dalla tua casella e poi premi «Segna come mandato», così resta nel Diario.
            </div>
          )}
          <Campo etichetta="A chi lo mandiamo">
            <div className="lv-dest">
              {data.destinatari.map((d) => (
                <label key={d.email} className="lv-dest-riga">
                  <input
                    type="checkbox"
                    className="lv-check"
                    checked={a.includes(d.email)}
                    onChange={(e) => setA((v) => (e.target.checked ? [...v, d.email] : v.filter((x) => x !== d.email)))}
                  />
                  <span className="lv-dest-nome">{d.nome}</span>
                  <span className="lv-dest-mail">{d.email}{d.ruolo ? ` · ${d.ruolo}` : ''}</span>
                </label>
              ))}
              <input
                className="inp"
                type="email"
                inputMode="email"
                placeholder="Un altro indirizzo email"
                value={altro}
                onChange={(e) => setAltro(e.target.value)}
              />
            </div>
          </Campo>
          <Campo etichetta="Oggetto della mail">
            <input className="inp" value={oggetto} onChange={(e) => setOggetto(e.target.value)} />
          </Campo>
          <Campo etichetta="Testo della mail" nota="Si può correggere: parte esattamente quello che leggi qui.">
            <textarea className="inp lv-area lv-area--testo" value={testo} onChange={(e) => setTesto(e.target.value)} />
          </Campo>
        </div>
      )}
    </Modal>
  );
}

function Campo({ etichetta, nota, children }: { etichetta: string; nota?: string; children: ReactNode }) {
  return (
    <div className="lv-campo">
      <div className="field-label">{etichetta}</div>
      {children}
      {nota && <div className="field-hint">{nota}</div>}
    </div>
  );
}
